import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paperImportApi, importLogApi } from './api';
import type { PaperImportSummary, PipelineStage, ImportLogEntry } from './types';

const STAGE_LABELS: Record<PipelineStage, string> = {
  uploaded: 'Uploaded',
  converting: 'Converting...',
  converted: 'Converted',
  extracting: 'Extracting...',
  extracted: 'Extracted',
  generating_solutions: 'Generating Solutions...',
  ready: 'Ready',
  imported: 'Imported',
};

const STAGE_COLORS: Record<PipelineStage, string> = {
  uploaded: '#6b7280',
  converting: '#f59e0b',
  converted: '#3b82f6',
  extracting: '#f59e0b',
  extracted: '#8b5cf6',
  generating_solutions: '#f59e0b',
  ready: '#10b981',
  imported: '#059669',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function PaperImports() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['paperImports'],
    queryFn: () => paperImportApi.list(),
  });

  const { data: importLogData } = useQuery({
    queryKey: ['importLog'],
    queryFn: () => importLogApi.list(50),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => paperImportApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['paperImports'] }),
  });

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '24px' }}>Imports</h1>
      </div>

      {/* Import Log */}
      {importLogData && importLogData.imports.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '16px', marginBottom: '12px', color: 'var(--text-secondary)' }}>Recent Imports</h2>
          <table className="dt dt--compact">
            <thead>
              <tr>
                <th>Type</th>
                <th>Source</th>
                <th>Exam</th>
                <th>Date</th>
                <th>Session</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>Created</th>
                <th style={{ textAlign: 'right' }}>Updated</th>
                <th style={{ textAlign: 'right' }}>Skipped</th>
                <th style={{ textAlign: 'right' }}>Failed</th>
                <th>Subjects</th>
                <th>Imported At</th>
              </tr>
            </thead>
            <tbody>
              {importLogData.imports.map((entry: ImportLogEntry) => (
                <tr key={entry.id}>
                  <td>
                    <span style={{
                      fontSize: '11px', padding: '2px 8px', borderRadius: '9999px', fontWeight: 500,
                      backgroundColor: entry.type === 'paper' ? 'var(--status-info-bg, #dbeafe)' : 'var(--status-success-bg, #f0fdf4)',
                      color: entry.type === 'paper' ? 'var(--color-info, #2563eb)' : 'var(--color-success, #16a34a)',
                    }}>
                      {entry.type === 'paper' ? 'PDF' : 'JSON'}
                    </span>
                  </td>
                  <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.source}
                  </td>
                  <td>{entry.exam || '-'}</td>
                  <td style={{ color: 'var(--text-tertiary)' }}>{entry.examDate || '-'}</td>
                  <td style={{ color: 'var(--text-tertiary)' }}>{entry.session || '-'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{entry.counts.total}</td>
                  <td style={{ textAlign: 'right', color: 'var(--color-success)' }}>{entry.counts.created || '-'}</td>
                  <td style={{ textAlign: 'right', color: 'var(--color-info)' }}>{entry.counts.updated || '-'}</td>
                  <td style={{ textAlign: 'right', color: 'var(--color-warning)' }}>{entry.counts.skipped || '-'}</td>
                  <td style={{ textAlign: 'right', color: entry.counts.failed > 0 ? 'var(--color-error)' : 'var(--text-tertiary)' }}>
                    {entry.counts.failed || '-'}
                  </td>
                  <td>
                    {entry.subjectCounts ? (
                      <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                        {Object.entries(entry.subjectCounts)
                          .map(([subj, c]) => `${subj}: ${c.created + c.updated}`)
                          .join(', ')}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ fontSize: '12px', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                    {new Date(entry.createdAt).toLocaleDateString()}{' '}
                    {new Date(entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 style={{ fontSize: '16px', marginBottom: '12px', color: 'var(--text-secondary)' }}>Paper Imports</h2>
      {/* List */}
      {isLoading && <p>Loading...</p>}
      {error && <p style={{ color: '#dc2626' }}>Error: {(error as Error).message}</p>}

      {data && data.imports.length === 0 && (
        <p style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '32px' }}>
          No paper imports.
        </p>
      )}

      {data && data.imports.length > 0 && (
        <table className="dt dt--compact">
          <thead>
            <tr>
              <th>File Name</th>
              <th>Size</th>
              <th>Exam</th>
              <th>Source</th>
              <th>Pages</th>
              <th>Questions</th>
              <th>Stage</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.imports.map((imp: PaperImportSummary) => (
              <tr key={imp.id}>
                <td>
                  {imp.fileName}
                  {imp.lastError && (
                    <span style={{ color: 'var(--color-error)', fontSize: '12px', display: 'block' }}>
                      Error: {imp.lastError.substring(0, 60)}...
                    </span>
                  )}
                </td>
                <td style={{ color: 'var(--text-tertiary)' }}>
                  {formatFileSize(imp.fileSizeBytes)}
                </td>
                <td>
                  {imp.exam || '-'}
                  {imp.examDate && <span style={{ color: 'var(--text-tertiary)', marginLeft: '4px' }}>({imp.examDate})</span>}
                </td>
                <td style={{ color: 'var(--text-tertiary)' }}>
                  {imp.source || '-'}
                </td>
                <td style={{ color: 'var(--text-tertiary)' }}>
                  {imp.totalPages || '-'}
                </td>
                <td style={{ color: 'var(--text-tertiary)' }}>
                  {imp.questionCount || 0}
                </td>
                <td>
                  <span style={{
                    fontSize: '12px',
                    padding: '2px 8px',
                    borderRadius: '9999px',
                    backgroundColor: STAGE_COLORS[imp.pipelineStage] + '20',
                    color: STAGE_COLORS[imp.pipelineStage],
                    fontWeight: 500,
                  }}>
                    {STAGE_LABELS[imp.pipelineStage]}
                  </span>
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => {
                      if (window.confirm('Delete this import and all associated data?')) {
                        deleteMutation.mutate(imp.id);
                      }
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
